import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, dateOnly, formatApiError } from "@/lib/api";
import { Building2, ChevronRight, Percent, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function Agreements() {
    const { user } = useAuth();
    const [list, setList] = useState([]);
    const [clients, setClients] = useState([]);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({
        client_id: "",
        type: "tarifario",
        sla_minutes: 30,
        credit_days: 30,
        credit_limit: 0,
        discount_pct: 0,
        start_date: new Date().toISOString().slice(0, 10),
        end_date: "",
        active: true,
        notes: "",
    });
    const isAdmin = ["superadmin", "direccion", "comercial", "gerente"].includes(user?.role);

    const load = async () => {
        try {
            const [a, c] = await Promise.all([api.get("/agreements"), api.get("/clients")]);
            setList(a.data || []);
            setClients(c.data || []);
        } catch (e) { console.warn(e); }
    };
    useEffect(() => { load(); }, []);

    const create = async () => {
        if (!form.client_id) return toast.error("Selecciona el cliente");
        if (!form.end_date) return toast.error("Captura la fecha de fin de vigencia");
        try {
            const client = clients.find((c) => c.id === form.client_id);
            await api.post("/agreements", {
                ...form,
                client_name: client?.business_name || "",
                rates: {},
            });
            toast.success("Convenio creado");
            setCreating(false);
            setForm({ ...form, client_id: "", end_date: "", notes: "" });
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const remove = async (a) => {
        if (!confirm(`¿Eliminar el convenio con ${a.client_name}? Esta acción no se puede deshacer.`)) return;
        try {
            await api.delete(`/agreements/${a.id}`);
            toast.success("Convenio eliminado");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    return (
        <div className="space-y-4" data-testid="agreements-page">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-sm text-slate-500">{list.length} convenio(s) registrado(s)</div>
                {isAdmin && (
                    <button onClick={() => setCreating(true)} className="fm-btn-primary" data-testid="new-agreement-btn">
                        <Plus className="w-3.5 h-3.5" /> Nuevo convenio
                    </button>
                )}
            </div>

            {creating && (
                <div className="fm-card p-4 border-l-4 border-l-red-500" data-testid="new-agreement-form">
                    <h3 className="font-display font-bold mb-3">Nuevo convenio</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Cliente</label>
                            <select className="fm-input" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} data-testid="agreement-client">
                                <option value="">Selecciona...</option>
                                {clients.map((c) => <option key={c.id} value={c.id}>{c.business_name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Tipo</label>
                            <select className="fm-input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                                <option value="tarifario">Tarifario</option>
                                <option value="por_evento">Por evento</option>
                                <option value="igualado">Igualado mensual</option>
                                <option value="bolsa">Bolsa de horas</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Inicio</label>
                            <input type="date" className="fm-input" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Fin</label>
                            <input type="date" className="fm-input" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">SLA (min)</label>
                            <input type="number" className="fm-input" value={form.sla_minutes} onChange={(e) => setForm({ ...form, sla_minutes: parseInt(e.target.value) || 0 })} />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Crédito (días)</label>
                            <input type="number" className="fm-input" value={form.credit_days} onChange={(e) => setForm({ ...form, credit_days: parseInt(e.target.value) || 0 })} />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Límite de crédito</label>
                            <input type="number" className="fm-input" value={form.credit_limit} onChange={(e) => setForm({ ...form, credit_limit: parseFloat(e.target.value) || 0 })} />
                        </div>
                        <div>
                            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Descuento %</label>
                            <input type="number" className="fm-input" value={form.discount_pct} onChange={(e) => setForm({ ...form, discount_pct: parseFloat(e.target.value) || 0 })} />
                        </div>
                        <div className="md:col-span-2">
                            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Notas</label>
                            <textarea className="fm-input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Observaciones, contactos, condiciones..." />
                        </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                        <button onClick={create} className="fm-btn-accent" data-testid="save-new-agreement">
                            <Plus className="w-3.5 h-3.5" /> Crear convenio
                        </button>
                        <button onClick={() => setCreating(false)} className="fm-btn-outline">Cancelar</button>
                    </div>
                    <div className="mt-2 text-[10px] text-slate-500 font-mono">
                        ⓘ Al crear el convenio entras al tarifario para capturar precios específicos por servicio. También puedes asignarle una Lista de Precios completa desde Clientes.
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {list.map((a) => {
                    const rateCount = Object.keys(a.rates || {}).length;
                    return (
                        <div key={a.id} className="fm-card p-4 hover:shadow-md transition-shadow block relative group" data-testid={`agreement-${a.id}`}>
                            {isAdmin && (
                                <button
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); remove(a); }}
                                    className="absolute top-2 right-2 p-1 text-slate-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                    data-testid={`delete-agreement-${a.id}`}
                                    title="Eliminar convenio"
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            )}
                            <Link to={`/agreements/${a.id}`}>
                                <div className="flex items-start justify-between mb-3">
                                    <div>
                                        <div className="text-[10px] uppercase tracking-widest font-bold text-red-600">{a.type}</div>
                                        <div className="font-display font-bold text-base mt-1">{a.client_name}</div>
                                    </div>
                                    <Building2 className="w-5 h-5 text-slate-300" strokeWidth={1.5} />
                                </div>
                                <div className="space-y-1 text-xs">
                                    <div className="flex justify-between"><span className="text-slate-500">Vigencia</span><span className="font-mono">{dateOnly(a.start_date)} → {dateOnly(a.end_date)}</span></div>
                                    <div className="flex justify-between"><span className="text-slate-500">SLA</span><span className="font-mono">{a.sla_minutes} min</span></div>
                                    <div className="flex justify-between"><span className="text-slate-500">Crédito</span><span className="font-mono">{a.credit_days} días</span></div>
                                    <div className="flex justify-between"><span className="text-slate-500">Descuento</span><span className="font-mono">{a.discount_pct}%</span></div>
                                    <div className="flex justify-between"><span className="text-slate-500">Tarifas configuradas</span><span className="font-mono font-bold">{rateCount}</span></div>
                                </div>
                                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                                    {a.active ? <span className="fm-badge fm-badge-positive">Activo</span> : <span className="fm-badge fm-badge-neutral">Inactivo</span>}
                                    <span className="text-[10px] text-slate-500 uppercase tracking-wider font-bold flex items-center gap-1">Editar tarifario <ChevronRight className="w-3 h-3" /></span>
                                </div>
                            </Link>
                        </div>
                    );
                })}
            </div>

            {list.length === 0 && !creating && (
                <div className="fm-card p-10 text-center text-sm text-slate-500">
                    <Percent className="w-10 h-10 mx-auto mb-2 text-slate-300" strokeWidth={1} />
                    Sin convenios todavía. {isAdmin && "Crea uno con el botón de arriba."}
                </div>
            )}
        </div>
    );
}
